import { useState, useRef, useEffect, useMemo } from 'react';

export interface CustomerItem {
  id: string;
  fullName: string;
  phone: string;
  email?: string;
  status?: string;
}

interface CustomerComboboxProps {
  customers: CustomerItem[];
  value: CustomerItem | null;
  onChange: (customer: CustomerItem | null) => void;
  onCreateNew: (name: string, phone: string) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
}

export default function CustomerCombobox({
  customers,
  value,
  onChange,
  onCreateNew,
  placeholder = 'Müşteri ara...',
  label,
  required = false,
  disabled = false,
}: CustomerComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [showNewForm, setShowNewForm] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return customers.slice(0, 8);
    const q = search.toLowerCase();
    return customers.filter(
      c =>
        c.fullName.toLowerCase().includes(q) ||
        c.phone.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q))
    );
  }, [customers, search]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowNewForm(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
    return undefined;
  }, [isOpen]);

  const handleSelect = (customer: CustomerItem) => {
    onChange(customer);
    setSearch('');
    setIsOpen(false);
    setShowNewForm(false);
  };

  const handleCreate = () => {
    if (!newName.trim() || !newPhone.trim()) return;
    onCreateNew(newName.trim(), newPhone.trim());
    setNewName('');
    setNewPhone('');
    setShowNewForm(false);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {label && (
        <label className="text-xs font-medium block mb-1.5" style={{ color: 'var(--text-secondary)' }}>
          {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      <button
        type="button"
        onClick={() => {
          if (!disabled) {
            setIsOpen(true);
            setSearch('');
            setTimeout(() => inputRef.current?.focus(), 50);
          }
        }}
        disabled={disabled}
        className="spa-input w-full flex items-center gap-2 text-left disabled:opacity-50"
      >
        {value ? (
          <>
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}
            >
              {value.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{value.fullName}</p>
              <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{value.phone}</p>
            </div>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-down-s-line text-sm" style={{ color: 'var(--text-muted)' }} />
            </div>
          </>
        ) : (
          <>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-sm" style={{ color: 'var(--text-muted)' }} />
            </div>
            <span className="text-sm flex-1" style={{ color: 'var(--text-muted)' }}>{placeholder}</span>
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-down-s-line text-sm" style={{ color: 'var(--text-muted)' }} />
            </div>
          </>
        )}
      </button>

      {isOpen && (
        <div
          className="absolute z-[120] left-0 right-0 mt-1 rounded-spa border overflow-hidden"
          style={{
            background: 'var(--card-bg)',
            borderColor: 'var(--border-color)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            maxHeight: '360px',
          }}
        >
          {/* Search input inside dropdown */}
          <div className="p-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
            <div className="relative">
              <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-xs" style={{ color: 'var(--text-muted)' }} />
              </div>
              <input
                ref={inputRef}
                type="text"
                className="spa-input w-full pl-8 text-sm"
                placeholder="İsim veya telefon ara..."
                value={search}
                onChange={e => { setSearch(e.target.value); setShowNewForm(false); }}
                onKeyDown={e => {
                  if (e.key === 'Escape') { setIsOpen(false); setShowNewForm(false); }
                }}
              />
            </div>
          </div>

          {/* Customer list */}
          <div className="overflow-y-auto" style={{ maxHeight: '240px' }}>
            {filtered.length > 0 ? (
              filtered.map(customer => (
                <button
                  key={customer.id}
                  type="button"
                  onClick={() => handleSelect(customer)}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-theme-hover transition-colors cursor-pointer"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(135deg, var(--gold), var(--gold-dark))' }}
                  >
                    {customer.fullName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{customer.fullName}</p>
                    <p className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{customer.phone}</p>
                  </div>
                  {value?.id === customer.id && (
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-sm" style={{ color: 'var(--gold)' }} />
                    </div>
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-center">
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Sonuç bulunamadı</p>
              </div>
            )}
          </div>

          {/* Add new customer toggle */}
          <div className="border-t p-2" style={{ borderColor: 'var(--border-color)' }}>
            {!showNewForm ? (
              <button
                type="button"
                onClick={() => {
                  setShowNewForm(true);
                  setNewName(search);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-spa-sm text-sm font-medium cursor-pointer transition-colors"
                style={{ background: 'rgba(201,169,98,0.08)', color: 'var(--gold)' }}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-add-line" />
                </div>
                {search.trim() ? `"${search}" isimli yeni müşteri ekle` : 'Yeni müşteri ekle'}
              </button>
            ) : (
              <div className="space-y-2 p-1">
                <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Yeni Müşteri Ekle</p>
                <input
                  className="spa-input w-full text-sm"
                  placeholder="Ad Soyad"
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                />
                <input
                  className="spa-input w-full text-sm"
                  placeholder="Telefon"
                  value={newPhone}
                  onChange={e => setNewPhone(e.target.value)}
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowNewForm(false)}
                    className="flex-1 spa-btn-secondary text-xs py-2"
                  >
                    İptal
                  </button>
                  <button
                    type="button"
                    onClick={handleCreate}
                    disabled={!newName.trim() || !newPhone.trim()}
                    className="flex-1 spa-btn-primary text-xs py-2 disabled:opacity-50"
                  >
                    <i className="ri-add-line mr-1" />Ekle
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}